import { useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { HERO_IMAGE } from "../lib/data";
import { openWhatsApp } from "../lib/whatsapp";

export default function Hero() {
    const bgRef = useRef(null);

    useEffect(() => {
        // Skip parallax on small screens & reduced-motion preference — it's
        // expensive and not noticeable on mobile.
        if (
            window.matchMedia("(max-width: 768px)").matches ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            return;
        }

        let ticking = false;
        let lastY = 0;
        const onScroll = () => {
            lastY = window.scrollY;
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    if (bgRef.current) {
                        bgRef.current.style.transform = `translate3d(0, ${lastY * 0.3}px, 0) scale(1.08)`;
                    }
                    ticking = false;
                });
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToPortfolio = () => {
        window.dispatchEvent(new Event("portfolio:open"));
    };

    return (
        <section
            id="inicio"
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
        >
            <div className="absolute inset-0 -z-10">
                <div
                    ref={bgRef}
                    className="absolute inset-0 parallax"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(250,250,248,0.62) 0%, rgba(250,250,248,0.55) 50%, rgba(250,250,248,0.85) 100%), url(${HERO_IMAGE})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        transform: "scale(1.08)",
                    }}
                />
            </div>

            <div className="relative z-10 max-w-5xl mx-auto px-6 text-center hero-fade no-select">
                <div className="flex justify-center mb-10">
                    <Logo size="lg" />
                </div>

                <div className="flex justify-center mb-8">
                    <span className="gold-line" aria-hidden="true" />
                </div>

                <h1
                    className="font-display italic text-[26px] sm:text-5xl lg:text-6xl font-light text-[#1C1A18] leading-tight px-2"
                    data-testid="hero-title"
                >
                    Cada momento merece ser eterno.
                </h1>

                <p
                    className="mt-6 sm:mt-8 max-w-2xl mx-auto text-[#6b665f] font-light text-sm sm:text-lg leading-relaxed px-2"
                    data-testid="hero-subtitle"
                >
                    Fotografía de autor para bodas, eventos y momentos que no
                    se repiten. Fotógrafa en Bilbao, Sestao, Portugalete y
                    Barakaldo — viajamos donde tú nos necesites.
                </p>

                <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
                    <button
                        type="button"
                        onClick={scrollToPortfolio}
                        data-testid="hero-view-work-btn"
                        className="w-full sm:w-auto px-8 py-4 btn-outline-ink text-[11px] uppercase"
                    >
                        Ver mi trabajo
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            openWhatsApp(
                                "Hola Silvia, me gustaría reservar una sesión contigo.",
                            )
                        }
                        data-testid="hero-whatsapp-btn"
                        className="w-full sm:w-auto px-8 py-4 btn-wa text-[11px] uppercase inline-flex items-center justify-center gap-3"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="-1 -1 26 26"
                            fill="currentColor"
                            aria-hidden="true"
                            style={{ display: "block" }}
                        >
                            <path d="M19.077 4.928A9.94 9.94 0 0012.012 2C6.486 2 1.989 6.494 1.985 12.018a9.94 9.94 0 001.337 4.998L2 22l5.13-1.345a9.97 9.97 0 004.778 1.218h.004c5.526 0 10.022-4.494 10.026-10.018a9.94 9.94 0 00-2.861-7.927zM12.012 20.18h-.003a8.31 8.31 0 01-4.234-1.158l-.304-.18-3.045.799.812-2.967-.198-.314a8.27 8.27 0 01-1.27-4.412c.002-4.598 3.745-8.34 8.347-8.34 2.228 0 4.323.87 5.898 2.448a8.28 8.28 0 012.443 5.896c-.002 4.598-3.745 8.34-8.346 8.34zm4.578-6.247c-.25-.125-1.486-.733-1.716-.817-.23-.084-.398-.125-.566.125-.167.25-.65.817-.797.984-.146.168-.293.188-.543.063-.25-.125-1.058-.39-2.016-1.245-.745-.665-1.248-1.485-1.395-1.735-.146-.25-.016-.385.11-.51.112-.112.25-.293.376-.439.125-.146.166-.25.25-.418.084-.167.042-.313-.02-.438-.063-.125-.566-1.364-.776-1.867-.205-.49-.412-.424-.566-.432-.146-.007-.314-.009-.481-.009a.93.93 0 00-.67.314c-.231.25-.881.86-.881 2.099 0 1.24.902 2.436 1.028 2.604.125.167 1.776 2.713 4.302 3.804.6.26 1.07.413 1.435.529.604.192 1.153.165 1.587.1.484-.072 1.486-.608 1.696-1.195.21-.586.21-1.089.147-1.194-.063-.105-.23-.168-.48-.293z" />
                        </svg>
                        WhatsApp directo
                    </button>
                </div>
            </div>

            <button
                type="button"
                onClick={scrollToPortfolio}
                aria-label="Bajar al portfolio"
                data-testid="hero-scroll-down"
                className="absolute bottom-14 sm:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#1C1A18]/70 hover:text-[#1C1A18]"
            >
                <span className="text-[10px] tracking-[0.4em] uppercase">
                    Descubre
                </span>
                <ChevronDown size={20} className="arrow-bounce" />
            </button>
        </section>
    );
}
